import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import type { Album, AlbumCatalogue, AlbumPage, AlbumSpread, AlbumStyles } from '../../types';
import { albumFonts, albumLook } from '../../utils/albumLook';
import { fontCss } from '../../utils/albumFonts';
import { pageWidth, spreadWidth } from '../../utils/albumSpreads';
import AlbumPageView from './AlbumPageView';

const MM_PER_PT = 0.3528;

type PageProps = ComponentProps<typeof AlbumPageView>;

interface Props extends Pick<PageProps, 'catalogue' | 'photosById' | 'summary' | 'maps' | 'trip' | 'styles' | 'compact'> {
  spread: AlbumSpread;
  albumStyle: Pick<Album, 'theme' | 'fontPair' | 'photoFrame' | 'spineText'>;
  selectedPageId?: string | null;
  selectedIndex?: number | null;
  /** Pressing anywhere on a page picks it; a slot then picks itself. */
  onSelectPage?: (pageId: string) => void;
  /** Everything a page's slots can do, for the selected page. */
  pageHandlers?: (page: AlbumPage) => Pick<PageProps, 'onSlotClick' | 'onPhotoDrop' | 'onPhotoRemove' | 'onPhotoMove' | 'onTextCommit'>;
}

const NO_LOOK = { background: null, backgroundPhotoId: null, backgroundAlbumPhotoId: null, backgroundVeil: 'dark' as const };

/**
 * ADR-0052: the book laid open. Two facing pages, one page across both, or the
 * cover: back, spine and front. The opening and closing pages face the
 * endpaper, drawn blank. The fold is drawn; nothing is placed across it.
 */
export default function AlbumSpreadView({
  spread, catalogue, albumStyle, styles, selectedPageId = null, selectedIndex = null, onSelectPage, pageHandlers, compact = false, ...rest
}: Props) {
  const { t } = useTranslation();
  const W = catalogue.page.width;
  const H = catalogue.page.height;
  const total = spreadWidth(catalogue, spread.kind);
  const pct = (mm: number) => `${(mm / total) * 100}%`;
  const cover = spread.kind === 'COVER';
  const spine = cover ? catalogue.spine.width : 0;
  const left = spread.pages.find(p => p.side === 'LEFT');
  const right = spread.pages.find(p => p.side === 'RIGHT');
  const both = spread.pages.find(p => p.side === 'BOTH');

  function page(p: AlbumPage, x: number) {
    const selected = p.id === selectedPageId;
    return (
      <div
        key={p.id}
        className={`absolute top-0 h-full ${!compact && selected ? 'z-10' : ''}`}
        style={{ left: pct(x), width: pct(pageWidth(catalogue, p, spread.kind)) }}
        onPointerDown={onSelectPage ? () => onSelectPage(p.id) : undefined}
      >
        <AlbumPageView
          {...rest}
          page={p}
          catalogue={catalogue}
          styles={styles}
          albumStyle={albumStyle}
          width={pageWidth(catalogue, p, spread.kind)}
          bare
          compact={compact}
          selectedIndex={selected ? selectedIndex : null}
          {...(!compact && pageHandlers ? pageHandlers(p) : {})}
        />
        {!compact && selected && <div className="pointer-events-none absolute inset-0 ring-2 ring-inset ring-teal-500/60" />}
      </div>
    );
  }

  const endpaper = (x: number) => (
    <div className="absolute top-0 h-full bg-slate-200/70 dark:bg-slate-700/60" style={{ left: pct(x), width: pct(W) }}
      title={compact ? undefined : t('album.spread.endpaper')} />
  );

  return (
    <div
      className="relative w-full shadow-sm ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden select-none bg-white"
      style={{ aspectRatio: `${total} / ${H}`, containerType: 'inline-size' }}
    >
      {both && page(both, 0)}
      {left && page(left, 0)}
      {right && page(right, W + spine)}
      {spread.kind === 'OPENING' && endpaper(0)}
      {spread.kind === 'CLOSING' && endpaper(W)}
      {cover && !compact && (
        <>
          <CoverTag x={0} width={W} total={total} label={t('album.spread.back')} />
          <CoverTag x={W + spine} width={W} total={total} label={t('album.spread.front')} />
        </>
      )}
      {cover
        ? <Spine x={W} width={spine} total={total} catalogue={catalogue} styles={styles} albumStyle={albumStyle} compact={compact} onWrap={!!both} />
        : <Fold total={total} at={W} guide={!compact && !!both} />}
    </div>
  );
}

/**
 * Laid open from the outside, the cover reads back–spine–front: the front is
 * the right half, which isn't what one guesses. Each half says which it is.
 */
function CoverTag({ x, width, total, label }: { x: number; width: number; total: number; label: string }) {
  return (
    <div className="pointer-events-none absolute z-20 top-1.5 flex justify-center" style={{ left: `${(x / total) * 100}%`, width: `${(width / total) * 100}%` }}>
      <span className="rounded-full bg-slate-900/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">{label}</span>
    </div>
  );
}

/** The middle of the open book: a shadow, and on a panorama the band no text may cross. */
function Fold({ total, at, guide }: { total: number; at: number; guide: boolean }) {
  const pct = (mm: number) => `${(mm / total) * 100}%`;
  return (
    <>
      <div className="pointer-events-none absolute z-20 top-0 h-full" style={{
        left: pct(at - 6), width: pct(12),
        background: 'linear-gradient(to right, transparent, rgba(15, 23, 42, 0.12) 50%, transparent)',
      }} />
      {guide && (
        <div className="pointer-events-none absolute z-20 top-0 h-full border-x border-dashed border-white/70 mix-blend-difference"
          style={{ left: pct(at - 5), width: pct(10) }} />
      )}
    </>
  );
}

/**
 * ADR-0052: the cover's spine, its width the server's estimate. The title runs
 * top to bottom (ISO 6357) on a band of the theme's paper, so it reads on a photo.
 */
function Spine({ x, width, total, catalogue, styles, albumStyle, compact, onWrap }: {
  x: number; width: number; total: number; catalogue: AlbumCatalogue; styles: AlbumStyles;
  albumStyle: Pick<Album, 'theme' | 'fontPair' | 'spineText'>; compact: boolean;
  /** Over the wrap's photo the spine is the photo: paper only behind a title. */
  onWrap: boolean;
}) {
  const look = albumLook(styles, albumStyle.theme, NO_LOOK);
  const fonts = albumFonts(styles, albumStyle.fontPair);
  const fits = width >= catalogue.spine.minForText;
  const text = fits ? albumStyle.spineText : null;
  return (
    <div
      className={`pointer-events-none absolute z-20 top-0 h-full flex items-center justify-center overflow-hidden ${compact ? '' : 'outline outline-1 outline-dashed outline-slate-400/70'}`}
      style={{
        left: `${(x / total) * 100}%`, width: `${(width / total) * 100}%`, color: look.ink.text,
        background: onWrap && !text ? 'transparent' : look.background.color,
        opacity: onWrap && text ? 0.9 : 1,
      }}
    >
      {text && (
        <span
          className="whitespace-nowrap"
          style={{
            writingMode: 'vertical-rl',
            fontSize: `${(catalogue.spine.fontPt * MM_PER_PT / total) * 100}cqw`,
            ...fontCss(fonts.title),
          }}
        >
          {text}
        </span>
      )}
    </div>
  );
}
